import url from '../../../../serverUrl';
import symbols from '../../../../symbolsMapper';
import { getCookie } from '../../../../utils';
import { fmtPrice, priceDecimals } from '../MarketStatsBar';

export { fmtPrice, priceDecimals };

export const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Quantities: more decimals for small sizes, grouped thousands for large.
export const fmtQty = (v, max) => {
  const n = num(v);
  if (n === null) return '—';
  const a = Math.abs(n);
  const d = max ?? (a >= 1000 ? 2 : a >= 1 ? 4 : 6);
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: d });
};

export const fmtUsd = (v, { compact = false } = {}) => {
  const n = num(v);
  if (n === null) return '—';
  if (compact) {
    const a = Math.abs(n);
    if (a >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (a >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
    if (a >= 1e4) return `$${(n / 1e3).toFixed(1)}K`;
  }
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const fmtPct = (v) => {
  const n = num(v);
  if (n === null) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
};

export const fmtTime = (ts, withDate = true) => {
  if (ts === undefined || ts === null || ts === '') return '—';
  const d = new Date(typeof ts === 'string' && /^\d+$/.test(ts) ? Number(ts) : ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  const time = d.toLocaleTimeString('en-GB', { hour12: false });
  if (!withDate) return time;
  const date = d.toLocaleDateString('en-CA'); // YYYY-MM-DD
  return `${date} ${time}`;
};

export const tokenLogo = (symbol) =>
  symbol === 'PECU'
    ? 'https://dev.hootdex.org/hootdex/images/pecuCoin.png'
    : `${url}/logo/tokenlogo/${symbols[symbol] ?? symbol}`;

export const displaySymbolOf = (token) =>
  token?.identitySymbol || token?.token_symbol || token?.symbol || '';

// Settlement (quote) asset per market - mirrors the backend exactly:
// settleMarketOrderPg / checkLimitOrderFunds (commonFunctions.js) settle a
// token in USXM only when its display symbol is in
// StableCoinIdentitySymbolList (commonUtils.js); everything else - USXM
// itself, the other XMG tokens, EquiTrack, Hybrid, DBT, Venture, Nexus -
// settles in PECU. /hootdex/market-tickers also returns `quote` per market
// from that same list; keep this copy in sync if the list changes.
export const USXM_SETTLED = new Set([
  'BRXM', 'WTXM', 'GAUXM', 'PDXM', 'PTXM', 'AGXM', 'AUXM', 'CUXM', 'CFXM',
  'CTXM', 'WHXM', 'CCXM', 'GSXM', 'CRXM', 'NGXM', 'JPXM', 'UKXM', 'EUXM'
]);
export const quoteSymbolFor = (displaySymbol) =>
  USXM_SETTLED.has(String(displaySymbol || '').trim()) ? 'USXM' : 'PECU';

export const quoteFor = (token, PecuPrice, USXMPrice) => {
  const usxm = quoteSymbolFor(displaySymbolOf(token)) === 'USXM';
  return usxm
    ? {
        symbol: 'USXM',
        logo: 'https://api.pecunovus.net/hootdex/images/image-1719842511599.png',
        price: num(USXMPrice)
      }
    : {
        symbol: 'PECU',
        logo: 'https://dev.hootdex.org/hootdex/images/pecuCoin.png',
        price: num(PecuPrice)
      };
};

// Same handshake the header's Connect button does: pick up the wallet token
// the Pecu Novus wallet left in the shared cookie, then reload.
export const connectWallet = () => {
  const tokenValue = getCookie('walletToken');
  if (tokenValue) {
    localStorage.setItem('hootdex_secretcookie_wallet', tokenValue);
    window.location.reload();
    return true;
  }
  window.open('https://www.pecunovus.net/signup', '_blank', 'noopener');
  return false;
};

// Order endpoints answer with a mix of strings and objects; decide whether
// the answer is a rejection so the toast can say so.
export const describeResult = (data) => {
  if (data === undefined || data === null || data === '') {
    return { ok: true, text: 'Order submitted' };
  }
  if (typeof data === 'string') {
    const bad = /insufficient|invalid|fail|error|not |unable|denied|missing/i.test(data);
    return { ok: !bad, text: data };
  }
  const text =
    data.message || data.msg || data.error || data.status_message ||
    (typeof data.status === 'string' ? data.status : null) ||
    JSON.stringify(data);
  const bad = data.error || data.status === false || data.success === false;
  return { ok: !bad, text: String(text) };
};

export const describeError = (err) => {
  const d = err?.response?.data;
  if (typeof d === 'string' && d) return d;
  if (d && (d.message || d.error)) return String(d.message || d.error);
  return err?.message || 'Request failed';
};

export const lsGet = (k, fallback) => {
  try {
    const v = window.localStorage.getItem(k);
    return v === null ? fallback : JSON.parse(v);
  } catch (e) {
    return fallback;
  }
};
export const lsSet = (k, v) => {
  try {
    window.localStorage.setItem(k, JSON.stringify(v));
  } catch (e) {
    /* storage unavailable - preference just won't persist */
  }
};
